import { skipToken, useQuery } from '@tanstack/react-query';
import React from 'react';

import type * as api from '@gitbook/api-client';
import { extractDeterministicJSONSchemaFromVisitorSchema } from '@gitbook/app-adaptive-content';
import { inferDefaultInputValuesFromObjectJSONSchema, SymbolsTable } from '@gitbook/expr';

import { apiQuery } from '../../state';
import { ExpressionProvider } from '../Expressions';
import { DocumentationLink } from '../Link';
import { useSpacePrimarySite } from '../Organization';

type SiteExpressionContextProviderProps =
    | { organizationId: api.Organization['id']; site: api.Site }
    | { space: api.Space };

/**
 * Provide site visitor specific adaptive content context to the expression editors.
 */
export function SiteVisitorExpressionContextProvider(
    props: React.PropsWithChildren<SiteExpressionContextProviderProps>
) {
    return 'site' in props ? (
        <SiteExpressionContextProviderWithSite {...props} />
    ) : (
        <SiteExpressionContextProviderWithSpace {...props} />
    );
}

/**
 * Provide site visitor specific adaptive content context to the condition expression editor in a site.
 *
 * Doesn't suspend to avoid blocking the rendering of the site.
 */
function SiteExpressionContextProviderWithSite(
    props: React.PropsWithChildren<{
        organizationId: api.Organization['id'];
        site: api.Site;
    }>
) {
    const { organizationId, site, children } = props;

    const hasAdaptiveContentEnabled = !!site.adaptiveContent?.enabled;

    const { data: siteSchema } = useQuery(
        apiQuery(
            'getSiteAdaptiveSchema',
            hasAdaptiveContentEnabled ? { organizationId, siteId: site.id } : skipToken,
            { nullOnError: true }
        )
    );

    const expressionEditorContext = React.useMemo(() => {
        const jsonSchema = siteSchema?.jsonSchema ?? {
            type: 'object',
            properties: {},
            additionalProperties: false,
        };
        const symbolsTable = getSymbolTableFromSiteJSONSchema(jsonSchema);
        const dynamicPropChecker = new DynamicPropChecker(symbolsTable, jsonSchema);
        return {
            symbolsTable,
            autocompleteFilter: (symbolPath: string | string[]) => {
                return dynamicPropChecker.isDynamicProperty(symbolPath)
                    ? {
                          exclusionMessage: (
                              <>
                                  Dynamic properties can't be used in this expression.{' '}
                                  <DocumentationLink
                                      path="publishing-documentation/adaptive-content"
                                      host="docs"
                                  >
                                      Learn more
                                  </DocumentationLink>
                              </>
                          ),
                      }
                    : false;
            },
        };
    }, [siteSchema]);

    if (!hasAdaptiveContentEnabled) {
        return <>{children}</>;
    }

    return <ExpressionProvider {...expressionEditorContext}>{children}</ExpressionProvider>;
}

/**
 * Provide site visitor specific adaptive content context to the inline expression editor in a space.
 *
 * Doesn't suspend to avoid blocking the rendering of the space.
 */
function SiteExpressionContextProviderWithSpace(
    props: React.PropsWithChildren<{ space: api.Space }>
) {
    const { space, children } = props;

    const { data: organizationContent } = useQuery(
        apiQuery('getOrganizationAllContent', {
            organizationId: space.organization,
        })
    );

    const spacePrimarySiteLookup = useSpacePrimarySite(organizationContent ?? null, space.id);
    const site = spacePrimarySiteLookup?.site;
    const hasAdaptiveContentEnabled = site && !!site.adaptiveContent?.enabled;

    const { data: siteSchema } = useQuery(
        apiQuery(
            'getSiteAdaptiveSchema',
            hasAdaptiveContentEnabled
                ? { organizationId: space.organization, siteId: site.id }
                : skipToken,
            { nullOnError: true }
        )
    );

    const expressionEditorContext = React.useMemo(() => {
        const jsonSchema = siteSchema?.jsonSchema ?? {
            type: 'object',
            properties: {},
            additionalProperties: false,
        };

        const symbolsTable = getSymbolTableFromSiteJSONSchema(jsonSchema);
        const dynamicPropChecker = new DynamicPropChecker(symbolsTable, jsonSchema);

        return {
            inputValues: {
                visitor: {
                    claims: inferDefaultInputValuesFromObjectJSONSchema(jsonSchema),
                    isSet: !!siteSchema,
                },
            },
            symbolsTable,
            autocompleteFilter: (symbolPath: string | string[]) => {
                return dynamicPropChecker.isDynamicProperty(symbolPath)
                    ? {
                          exclusionMessage: (
                              <>
                                  Dynamic properties can't be used in this expression.{' '}
                                  <DocumentationLink
                                      path="publishing-documentation/adaptive-content"
                                      host="docs"
                                  >
                                      Learn more
                                  </DocumentationLink>
                              </>
                          ),
                      }
                    : false;
            },
        };
    }, [siteSchema]);

    if (!hasAdaptiveContentEnabled) {
        return <>{children}</>;
    }

    return <ExpressionProvider {...expressionEditorContext}>{children}</ExpressionProvider>;
}

function getSymbolTableFromSiteJSONSchema(siteJSONSchema: api.SiteAdaptiveJSONSchema) {
    return new SymbolsTable({
        visitor: SymbolsTable.inferSymbolFromJSONSchema(
            {
                type: 'object',
                description: `The attributes tied to the site's visitor.`,
                properties: {
                    claims: {
                        ...siteJSONSchema,
                        description: 'The attributes tied set on the site visitor.',
                    },
                    isSet: {
                        type: 'boolean',
                        description: 'Whether the visitor has claims set.',
                    },
                },
                required: ['claims', 'isSet'],
            },
            'visitor'
        ),
    });
}

/**
 * Wrapper around a symbol table that provides utility to check if a symbol is a dynamic (non-deterministic) property.
 */
class DynamicPropChecker {
    private fullSchemaSymbolTable: SymbolsTable;
    private deterministicSymbolsTable: SymbolsTable;

    constructor(fullSchemaSymbolTable: SymbolsTable, siteJSONSchema: api.SiteAdaptiveJSONSchema) {
        this.fullSchemaSymbolTable = fullSchemaSymbolTable;
        this.deterministicSymbolsTable = getSymbolTableFromSiteJSONSchema(
            extractDeterministicJSONSchemaFromVisitorSchema(siteJSONSchema)
        );
    }

    /**
     * Returns true if a symbol at a given path corresponds to a dynamic schema property.
     */
    isDynamicProperty(path: string | string[]): boolean {
        const baseSymbol = this.fullSchemaSymbolTable.getSymbolInfo(path);
        if (!baseSymbol) {
            return false;
        }

        const deterministicSymbol = this.deterministicSymbolsTable.getSymbolInfo(path);
        return deterministicSymbol === undefined;
    }
}
